import React, { useState } from 'react';
import {
  Users,
  Video,
  DollarSign,
  Activity,
  ArrowUpRight,
  Search,
  Trash2,
  Shield,
  CheckCircle,
  XCircle,
  Sparkles,
  Zap,
} from 'lucide-react';

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: 'Super Admin' | 'Editor' | 'Member';
  status: 'active' | 'suspended' | 'pending';
  lastActive: string;
  avatarColor: string;
}

const INITIAL_USERS: UserItem[] = [
  {
    id: 'USR-01',
    name: 'Nguyễn Văn An',
    email: 'an.nguyen@company.com',
    role: 'Super Admin',
    status: 'active',
    lastActive: '2 phút trước',
    avatarColor: 'from-blue-600 to-indigo-600',
  },
  {
    id: 'USR-02',
    name: 'Trần Thị Mai',
    email: 'mai.tran@company.com',
    role: 'Editor',
    status: 'active',
    lastActive: '15 phút trước',
    avatarColor: 'from-purple-600 to-pink-600',
  },
  {
    id: 'USR-03',
    name: 'Lê Hoàng Long',
    email: 'long.le@company.com',
    role: 'Editor',
    status: 'pending',
    lastActive: 'Hôm qua',
    avatarColor: 'from-emerald-600 to-teal-600',
  },
  {
    id: 'USR-04',
    name: 'Phạm Quốc Bảo',
    email: 'bao.pham@partner.vn',
    role: 'Member',
    status: 'suspended',
    lastActive: '3 ngày trước',
    avatarColor: 'from-amber-600 to-orange-600',
  },
  {
    id: 'USR-05',
    name: 'Đặng Thảo Nhi',
    email: 'nhi.dang@company.com',
    role: 'Member',
    status: 'active',
    lastActive: '1 giờ trước',
    avatarColor: 'from-cyan-600 to-blue-600',
  },
];

export const AdminDashboardPage: React.FC = () => {
  const [users, setUsers] = useState<UserItem[]>(INITIAL_USERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'pending'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'Super Admin' | 'Editor' | 'Member'>('Editor');

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleToggleStatus = (id: string) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (u.id === id) {
          const nextStatus = u.status === 'active' ? 'suspended' : 'active';
          return { ...u, status: nextStatus };
        }
        return u;
      })
    );
  };

  const handleDeleteUser = (id: string) => {
    if (window.confirm('Bạn có chắc muốn xoá người dùng này?')) {
      setUsers((prev) => prev.filter((u) => u.id !== id));
    }
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim()) return;

    const newUser: UserItem = {
      id: `USR-0${users.length + 1}`,
      name: newUserName.trim(),
      email: newUserEmail.trim(),
      role: newUserRole,
      status: 'active',
      lastActive: 'Vừa xong',
      avatarColor: 'from-blue-500 to-cyan-500',
    };

    setUsers([newUser, ...users]);
    setNewUserName('');
    setNewUserEmail('');
    setShowAddModal(false);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-blue-500/20 bg-gradient-to-r from-blue-900/40 via-[#101b33] to-[#0d1527] p-6 shadow-xl">
        <div className="pointer-events-none absolute -right-10 -bottom-10 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 border border-blue-500/30 px-3 py-1 text-xs font-semibold text-blue-300">
              <Sparkles className="h-3.5 w-3.5 text-blue-400" />
              <span>PNR Client Ecosystem • Quản Trị Hệ Thống</span>
            </div>
            <h2 className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tight text-white">
              Chào mừng bạn đến với PNR Admin Workspace!
            </h2>
            <p className="mt-1 text-xs sm:text-sm text-slate-300 max-w-xl">
              Thanh menu quản trị nằm ở <span className="font-semibold text-blue-400 underline">bên trái</span> gồm 2 chức năng trọng tâm: <span className="text-white font-medium">Tổng quan hệ thống</span> và <span className="text-white font-medium">Quản lý phiên bản app desktop</span>.
            </p>
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <a
              href="/admin/app-versions"
              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-500/20 hover:from-blue-500 hover:to-indigo-500 transition-all cursor-pointer"
            >
              <span>Xem Phiên Bản App Desktop</span>
            </a>
          </div>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {/* Metric 1 */}
        <div className="group rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-5 shadow-lg backdrop-blur-md transition-all hover:border-blue-500/40 hover:bg-[#121c33]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Tổng Doanh Thu
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <DollarSign className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-white tracking-tight">$142,850</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-400">
              <ArrowUpRight className="h-3.5 w-3.5" /> +16.4%
            </span>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Tăng $20.2k so với tháng trước</p>
        </div>

        {/* Metric 2 */}
        <div className="group rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-5 shadow-lg backdrop-blur-md transition-all hover:border-indigo-500/40 hover:bg-[#121c33]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Người Dùng Hoạt Động
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
              <Users className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-white tracking-tight">18,420</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-400">
              <ArrowUpRight className="h-3.5 w-3.5" /> +8.2%
            </span>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">+1,240 người đăng ký mới tuần này</p>
        </div>

        {/* Metric 3 */}
        <div className="group rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-5 shadow-lg backdrop-blur-md transition-all hover:border-amber-500/40 hover:bg-[#121c33]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Video & AI Jobs
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Video className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-white tracking-tight">3,892</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-emerald-400">
              <ArrowUpRight className="h-3.5 w-3.5" /> +24.8%
            </span>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Thời gian render TB: 4.2s / video</p>
        </div>

        {/* Metric 4 */}
        <div className="group rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-5 shadow-lg backdrop-blur-md transition-all hover:border-emerald-500/40 hover:bg-[#121c33]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Trạng Thái Hệ Thống
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <Activity className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-2xl font-extrabold text-emerald-400 tracking-tight">99.98%</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-slate-400">
              Ổn định
            </span>
          </div>
          <p className="mt-1 text-[11px] text-slate-400">12 nodes trực tuyến • 0 lỗi nghiêm trọng</p>
        </div>
      </div>

      {/* Analytics & Resource Section */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Chart Column (2 Cols) */}
        <div className="rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-6 shadow-xl backdrop-blur-md lg:col-span-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-800/80 pb-4">
            <div>
              <h3 className="text-sm font-bold text-white tracking-tight">
                Hiệu suất xử lý & Lưu lượng truy cập
              </h3>
              <p className="text-xs text-slate-400">Thống kê dữ liệu tuần qua theo ngày</p>
            </div>
            <div className="flex items-center gap-1.5 rounded-xl border border-slate-800 bg-[#121a2d] p-1 text-xs">
              <button className="rounded-lg bg-blue-600 px-2.5 py-1 font-semibold text-white">
                7 Ngày
              </button>
              <button className="rounded-lg px-2.5 py-1 text-slate-400 hover:text-white">
                30 Ngày
              </button>
              <button className="rounded-lg px-2.5 py-1 text-slate-400 hover:text-white">
                Quý
              </button>
            </div>
          </div>

          {/* Bar Chart Visualization */}
          <div className="mt-6 flex h-48 items-end gap-3 sm:gap-6 px-2">
            {[
              { day: 'T2', height: '65%', val: '2.4k' },
              { day: 'T3', height: '80%', val: '3.1k' },
              { day: 'T4', height: '45%', val: '1.8k' },
              { day: 'T5', height: '90%', val: '3.6k' },
              { day: 'T6', height: '75%', val: '2.9k' },
              { day: 'T7', height: '98%', val: '4.2k' },
              { day: 'CN', height: '70%', val: '2.7k' },
            ].map((bar, idx) => (
              <div key={idx} className="group relative flex flex-1 flex-col items-center gap-2 h-full justify-end">
                <div className="opacity-0 group-hover:opacity-100 absolute -top-7 text-[10px] font-bold text-white bg-slate-800 px-2 py-0.5 rounded transition-opacity">
                  {bar.val}
                </div>
                <div className="w-full rounded-t-xl bg-slate-800/60 overflow-hidden flex items-end">
                  <div
                    style={{ height: bar.height }}
                    className="w-full rounded-t-xl bg-gradient-to-t from-blue-600 via-indigo-500 to-cyan-400 transition-all duration-500 group-hover:brightness-125"
                  />
                </div>
                <span className="text-[11px] font-medium text-slate-400">{bar.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Server & Resource Monitor (1 Col) */}
        <div className="rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-6 shadow-xl backdrop-blur-md">
          <h3 className="text-sm font-bold text-white tracking-tight flex items-center justify-between">
            <span>Tài Nguyên Hệ Thống</span>
            <Zap className="h-4 w-4 text-amber-400" />
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">Tải phần cứng thời gian thực</p>

          <div className="mt-5 space-y-4">
            {/* CPU */}
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-300 font-medium">CPU (8 Cores)</span>
                <span className="text-blue-400 font-semibold">38%</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full w-[38%] rounded-full bg-blue-500" />
              </div>
            </div>

            {/* RAM */}
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-300 font-medium">Bộ nhớ RAM (16GB)</span>
                <span className="text-indigo-400 font-semibold">62%</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full w-[62%] rounded-full bg-indigo-500" />
              </div>
            </div>

            {/* GPU AI */}
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-300 font-medium">GPU Tensor AI</span>
                <span className="text-amber-400 font-semibold">84%</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full w-[84%] rounded-full bg-amber-500" />
              </div>
            </div>

            {/* Storage */}
            <div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-300 font-medium">SSD NVMe Cloud</span>
                <span className="text-emerald-400 font-semibold">41%</span>
              </div>
              <div className="mt-1.5 h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full w-[41%] rounded-full bg-emerald-500" />
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-slate-800 bg-[#121a2d] p-3 text-xs text-slate-300 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping shrink-0" />
            <span>Tất cả cụm máy chủ phản hồi bình thường (&lt; 25ms)</span>
          </div>
        </div>
      </div>

      {/* User Management Section */}
      <div className="rounded-2xl border border-slate-800/80 bg-[#0f172a]/70 p-6 shadow-xl backdrop-blur-md">
        {/* Table Controls Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
              <span>Danh Sách Người Dùng & Phân Quyền</span>
              <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-400 font-semibold border border-blue-500/20">
                {filteredUsers.length} tài khoản
              </span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Quản lý tài khoản, thay đổi quyền hạn và phân quyền hệ thống
            </p>
          </div>

          {/* Filters & Search */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Input */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tìm tên hoặc email..."
                className="rounded-xl border border-slate-700/80 bg-[#131d33] py-1.5 pr-3 pl-8 text-xs text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="flex items-center rounded-xl border border-slate-700/80 bg-[#131d33] p-1 text-xs">
              <button
                onClick={() => setStatusFilter('all')}
                className={`rounded-lg px-2.5 py-1 font-medium transition-colors ${
                  statusFilter === 'all' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Tất cả
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`rounded-lg px-2.5 py-1 font-medium transition-colors ${
                  statusFilter === 'active' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Hoạt động
              </button>
              <button
                onClick={() => setStatusFilter('suspended')}
                className={`rounded-lg px-2.5 py-1 font-medium transition-colors ${
                  statusFilter === 'suspended' ? 'bg-red-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Tạm khoá
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[11px]">
                <th className="py-3 px-4">Người dùng</th>
                <th className="py-3 px-4">Vai trò</th>
                <th className="py-3 px-4">Trạng thái</th>
                <th className="py-3 px-4">Hoạt động gần nhất</th>
                <th className="py-3 px-4 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-slate-800/30 transition-colors">
                  {/* User info */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr ${user.avatarColor} font-bold text-white shadow-sm`}
                      >
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{user.name}</div>
                        <div className="text-[11px] text-slate-400">{user.email}</div>
                      </div>
                    </div>
                  </td>

                  {/* Role */}
                  <td className="py-3.5 px-4">
                    <span className="inline-flex items-center gap-1 rounded-lg border border-slate-700/80 bg-slate-800/60 px-2.5 py-1 font-medium text-slate-200">
                      <Shield className="h-3 w-3 text-blue-400" />
                      {user.role}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-3.5 px-4">
                    {user.status === 'active' && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 font-medium text-emerald-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                        Đang hoạt động
                      </span>
                    )}
                    {user.status === 'suspended' && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/10 border border-red-500/20 px-2.5 py-0.5 font-medium text-red-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                        Bị tạm khoá
                      </span>
                    )}
                    {user.status === 'pending' && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 px-2.5 py-0.5 font-medium text-amber-400">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                        Chờ duyệt
                      </span>
                    )}
                  </td>

                  {/* Last active */}
                  <td className="py-3.5 px-4 text-slate-400">{user.lastActive}</td>

                  {/* Actions */}
                  <td className="py-3.5 px-4 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(user.id)}
                        title={user.status === 'active' ? 'Khoá tài khoản' : 'Kích hoạt tài khoản'}
                        className={`p-1.5 rounded-lg border transition-colors ${
                          user.status === 'active'
                            ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                            : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                        }`}
                      >
                        {user.status === 'active' ? (
                          <XCircle className="h-3.5 w-3.5" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Xoá người dùng"
                        className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Add User */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-[#0f172a] p-6 shadow-2xl">
            <h3 className="text-base font-bold text-white">Thêm thành viên mới</h3>
            <p className="mt-1 text-xs text-slate-400">
              Nhập thông tin người dùng để phân quyền vào hệ thống quản trị.
            </p>

            <form onSubmit={handleAddUser} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300">Họ và tên</label>
                <input
                  type="text"
                  required
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Ví dụ: Lê Minh Hoàng"
                  className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300">Địa chỉ Email</label>
                <input
                  type="email"
                  required
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="hoang.le@company.com"
                  className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300">Vai trò</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as any)}
                  className="mt-1 w-full rounded-xl border border-slate-700 bg-[#131d33] px-3 py-2 text-xs text-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="Super Admin">Super Admin</option>
                  <option value="Editor">Editor</option>
                  <option value="Member">Member</option>
                </select>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  Huỷ
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-500 transition-colors"
                >
                  Thêm người dùng
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
